<div class="section over-hide padding-top-bottom-120 section-background-24 help-faq-page">
    <div class="section-1400 pt-xl-4">
        <div class="container-fluid">
            <div class="section border-4 bg-white landing-shadow-4 p-3 p-xl-4">
                <div class="guide-header">
                    <h3 class="mb-1">Rajputra.org User Guide</h3>
                    <p class="guide-meta mb-0">Version 1.1 | Last Updated: March 1, 2026</p>
                    <p class="guide-meta mb-0">Scope: Web application workflows, role usage, and submenu guidance</p>
                </div>

                <div class="guide-toc mt-4">
                    <h5>Table of Contents</h5>
                    <div class="toc-grid">
                        <button type="button" class="toc-link" (click)="scrollToSection('section-1')">1. Registration and Membership</button>
                        <button type="button" class="toc-link" (click)="scrollToSection('section-2')">2. Login, Password, and Profile</button>
                        <button type="button" class="toc-link" (click)="scrollToSection('section-3')">3. Top Menu Navigation</button>
                        <button type="button" class="toc-link" (click)="scrollToSection('section-4')">4. Chapters and Chapter Home</button>
                        <button type="button" class="toc-link" (click)="scrollToSection('section-5')">5. Event Management</button>
                        <button type="button" class="toc-link" (click)="scrollToSection('section-6')">6. Event Registration and Payments</button>
                        <button type="button" class="toc-link" (click)="scrollToSection('section-7')">7. Event Communication, Expenses, and Photos</button>
                        <button type="button" class="toc-link" (click)="scrollToSection('section-8')">8. NEC and Chapter Communication</button>
                        <button type="button" class="toc-link" (click)="scrollToSection('section-9')">9. Matrimonial and Business Directory</button>
                        <button type="button" class="toc-link" (click)="scrollToSection('section-10')">10. Media, Downloads, and MoM</button>
                        <button type="button" class="toc-link" (click)="scrollToSection('section-11')">11. Roles and Permissions</button>
                        <button type="button" class="toc-link" (click)="scrollToSection('section-12')">12. FAQ and Troubleshooting</button>
                    </div>
                </div>

                <section id="section-1" class="guide-section">
                    <h5>1. Registration and Membership</h5>
                    <p class="text-muted mb-2">How a new user joins the platform and becomes an approved member.</p>
                    <ol>
                        <li>Open the site and click <strong>Become a new member</strong>.</li>
                        <li>Enter required details such as first name, last name, email, and mobile.</li>
                        <li>Create password (minimum 6 characters) and submit registration.</li>
                        <li>Select membership option: <strong>Life Membership</strong> or <strong>Annual Membership</strong>.</li>
                        <li>Complete payment through Stripe on the membership payment page.</li>
                        <li>After successful payment, account remains pending until NEC approval.</li>
                        <li>NEC can approve members from NEC user-management workflows.</li>
                    </ol>
                    <p class="mb-0"><strong>Note:</strong> Offline/alternate payment scenarios can be approved manually by NEC after verification.</p>
                </section>

                <section id="section-2" class="guide-section">
                    <h5>2. Login, Password, and Profile</h5>
                    <h6>2.1 Login</h6>
                    <ol>
                        <li>Use <strong>Sign In</strong> from header.</li>
                        <li>Enter email and password.</li>
                        <li>Click <strong>Login</strong> to enter your account.</li>
                    </ol>
                    <h6>2.2 Forgot Password</h6>
                    <ol>
                        <li>Click <strong>Forgot your password?</strong> on login page.</li>
                        <li>Provide registered email.</li>
                        <li>Use recovery password from email to log in.</li>
                        <li>Immediately change password from profile settings.</li>
                    </ol>
                    <h6>2.3 Profile</h6>
                    <ol>
                        <li>Go to profile after login.</li>
                        <li>Update personal details and profile information.</li>
                        <li>Use change-password flow to set a new secure password.</li>
                    </ol>
                </section>

                <section id="section-3" class="guide-section">
                    <h5>3. Top Menu Navigation</h5>
                    <p>The main header contains primary modules:</p>
                    <ul>
                        <li><strong>About Us</strong>: organization information and governance pages.</li>
                        <li><strong>Chapters</strong>: chapter-specific operations and chapter home pages.</li>
                        <li><strong>Community Services</strong>: matrimonial and business/community programs.</li>
                        <li><strong>Events</strong>: event listings and event detail pages.</li>
                        <li><strong>Media and Downloads</strong>: documents, resources, and chapter/NEC content.</li>
                    </ul>
                    <p class="mb-0"><strong>Navigation Tip:</strong> Use page breadcrumbs to jump back to chapter home and parent pages.</p>
                </section>

                <section id="section-4" class="guide-section">
                    <h5>4. Chapters and Chapter Home</h5>
                    <p>Open a chapter and use the left panel to manage chapter-level sections.</p>
                    <h6>4.1 Chapter Home Sections</h6>
                    <ul>
                        <li><strong>President's Note</strong>: view and update chapter welcome message.</li>
                        <li><strong>Executive Committee</strong>: manage chapter committee details.</li>
                        <li><strong>Board of Trustees</strong>: manage BOT records.</li>
                        <li><strong>Events</strong>: create and edit chapter events.</li>
                        <li><strong>Minutes of Meeting</strong>: maintain chapter MoM entries.</li>
                        <li><strong>User Profile Update</strong>: NEC-only profile update tools.</li>
                        <li><strong>Member Directory</strong>: chapter user list with membership status.</li>
                        <li><strong>Search Users</strong>: chapter-scoped user lookup and detail review.</li>
                        <li><strong>Communication</strong>: send chapter or NEC-targeted communication.</li>
                    </ul>
                    <h6>4.2 Member Directory and Search</h6>
                    <ol>
                        <li>Open <strong>Member Directory</strong> to list users in chapter.</li>
                        <li>Filter by name/email from the search box.</li>
                        <li>Click first/last name hyperlink to open <strong>Search Users</strong> panel with profile details.</li>
                    </ol>
                </section>

                <section id="section-5" class="guide-section">
                    <h5>5. Event Management</h5>
                    <h6>5.1 Create Event</h6>
                    <ol>
                        <li>Navigate to <strong>Chapters -> [Your Chapter] -> Events</strong>.</li>
                        <li>Click <strong>Add Event</strong>.</li>
                        <li>Fill title, location, start/end date-time, type, and status.</li>
                        <li>For paid events, enter adult/child/senior fees.</li>
                        <li>Set <strong>Child Minimum Age</strong> and <strong>Child Maximum Age</strong> for paid events.</li>
                        <li>Children between minimum and maximum ages are counted under Children.</li>
                        <li>Ages above the maximum are counted under Adults.</li>
                        <li>Save event.</li>
                    </ol>
                    <h6>5.2 Update Event</h6>
                    <ol>
                        <li>Open event detail page from chapter event list.</li>
                        <li>Use <strong>Edit</strong> in Event Details section.</li>
                        <li>Update title, description, schedule, status, and paid-event rules.</li>
                        <li>Save changes and verify updated values in event detail view.</li>
                    </ol>
                </section>

                <section id="section-6" class="guide-section">
                    <h5>6. Event Registration and Payments</h5>
                    <h6>6.1 User Registration</h6>
                    <ol>
                        <li>Open event and click <strong>Register</strong>.</li>
                        <li>Enter attendee counts: adults, children, seniors.</li>
                        <li>For paid events, enter only children within configured min and max ages in the Children count.</li>
                        <li>Count children above configured maximum age in Adults count.</li>
                        <li>Child ages can be entered in optional list for reference only.</li>
                        <li>Optionally add donation amount.</li>
                        <li>Confirm registration and proceed to payment checkout.</li>
                    </ol>
                    <h6>6.2 Amount Calculation Rules</h6>
                    <ul>
                        <li>Adults are always billed using adult fee.</li>
                        <li>Children count should represent ages between configured min and max.</li>
                        <li>Any child above the max age must be included in Adults count.</li>
                        <li>Seniors are billed with senior fee when configured.</li>
                        <li>Donation is added on top of event fees.</li>
                    </ul>
                    <h6>6.3 Organizer View</h6>
                    <ul>
                        <li><strong>Event Registrations</strong> section lists confirmed and pending registrations.</li>
                        <li>Donation contributors are shown separately in donation summary.</li>
                    </ul>
                </section>

                <section id="section-7" class="guide-section">
                    <h5>7. Event Communication, Expenses, and Photos</h5>
                    <h6>7.1 Email Communication</h6>
                    <ol>
                        <li>Open <strong>Email Communication</strong> in event left panel.</li>
                        <li>Click <strong>Add Communication</strong> to create a template message.</li>
                        <li>Update, test, and send communication to registrants.</li>
                    </ol>
                    <h6>7.2 Expenses and Receipts</h6>
                    <ol>
                        <li>Open <strong>Expenses</strong> section.</li>
                        <li>Upload expense receipt (PDF/JPG/PNG as supported).</li>
                        <li>System creates line item; enter category and amount.</li>
                        <li>Review total expenses, total collection, and profit.</li>
                    </ol>
                    <h6>7.3 Photos</h6>
                    <ol>
                        <li>Use <strong>Photos</strong> section to upload event pictures.</li>
                        <li>Review and manage uploaded content.</li>
                    </ol>
                </section>

                <section id="section-8" class="guide-section">
                    <h5>8. NEC and Chapter Communication</h5>
                    <h6>8.1 Chapter Executive Communication</h6>
                    <ul>
                        <li>Chapter executive can send messages to chapter users.</li>
                        <li>Can include members only or include non-members.</li>
                    </ul>
                    <h6>8.2 NEC Communication</h6>
                    <ul>
                        <li>NEC can target <strong>All Chapters</strong> or <strong>Specific Chapters</strong>.</li>
                        <li>NEC can choose to include or exclude non-members.</li>
                        <li>Delivery summary shows delivered and failed counts.</li>
                    </ul>
                </section>

                <section id="section-9" class="guide-section">
                    <h5>9. Matrimonial and Business Directory</h5>
                    <h6>9.1 Matrimonial</h6>
                    <ul>
                        <li>Create and maintain profile details, partner preferences, and visibility status.</li>
                        <li>Upload profile photo and keep details current.</li>
                        <li>Admin/authorized reviewers can approve moderation workflows where enabled.</li>
                    </ul>
                    <h6>9.2 Business Directory</h6>
                    <ul>
                        <li>Members can add business listing with contact and category details.</li>
                        <li>Authorized users can edit or delete listings.</li>
                        <li>Listings are visible to logged-in users.</li>
                    </ul>
                </section>

                <section id="section-10" class="guide-section">
                    <h5>10. Media, Downloads, and MoM</h5>
                    <ul>
                        <li>Chapter MoM is accessible under chapter page left panel.</li>
                        <li>NEC MoM is available through <strong>Media and Downloads -> NEC Minutes of Meeting</strong>.</li>
                        <li>MoM visibility can be controlled based on configuration and role.</li>
                    </ul>
                </section>

                <section id="section-11" class="guide-section">
                    <h5>11. Roles and Permissions</h5>
                    <div class="role-grid">
                        <div class="role-card">
                            <h6>NEC / NEC President</h6>
                            <p>Cross-chapter administration, membership approvals, NEC communication, and governance updates.</p>
                        </div>
                        <div class="role-card">
                            <h6>Chapter President / CEC</h6>
                            <p>Chapter-level updates: committee, events, MoM, chapter communication, and chapter member tools.</p>
                        </div>
                        <div class="role-card">
                            <h6>Member</h6>
                            <p>Profile management, event participation, community services, and member-level content access.</p>
                        </div>
                        <div class="role-card">
                            <h6>Non Member</h6>
                            <p>Limited access until payment and approval are complete; can be included/excluded in communication based on sender scope.</p>
                        </div>
                    </div>
                </section>

                <section id="section-12" class="guide-section guide-section-last">
                    <h5>12. FAQ and Troubleshooting</h5>
                    <h6>12.1 Common Questions</h6>
                    <p><strong>Q: Password reset is not working.</strong><br>
                        A: Confirm email is registered; check spam/junk folder for recovery email.</p>
                    <p><strong>Q: Membership still pending after payment.</strong><br>
                        A: Approval is completed by NEC workflow; contact NEC if delay is long.</p>
                    <p><strong>Q: Event save returns error.</strong><br>
                        A: Verify mandatory fields, status value, and date/time format; for paid events validate fee inputs and child-age rule fields.</p>
                    <p><strong>Q: Event email did not reach users.</strong><br>
                        A: Check communication panel delivery summary and confirm recipients exist for selected scope.</p>
                    <p><strong>Q: Receipt upload failed.</strong><br>
                        A: Use supported file format and size limits; retry upload before adding line item details.</p>

                    <h6>12.2 Support Path</h6>
                    <ol>
                        <li>Use <strong>Contact Us</strong> page for support requests.</li>
                        <li>Provide module name, exact page, and timestamp of issue.</li>
                        <li>Include screenshot and user email for faster triage.</li>
                    </ol>
                </section>
            </div>
        </div>
    </div>
</div>
